import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  ArrowUpRight,
  ExternalLink,
  FolderKanban,
  Loader2,
  Camera,
  Film,
  Package,
  Music,
  Airplay,
  Sparkles,
  Globe,
} from "lucide-react";
import { projectsApi, studioApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useBrand } from "@/lib/brand";
import ProductivityPulseWidget from "@/components/launchpad/ProductivityPulseWidget";
import MyPlateWidget from "@/components/launchpad/MyPlateWidget";
import WaitingOnWidget from "@/components/launchpad/WaitingOnWidget";
import AtRiskWidget from "@/components/launchpad/AtRiskWidget";
import StudioInsightsWidget from "@/components/launchpad/StudioInsightsWidget";
import TeamCapacityWidget from "@/components/launchpad/TeamCapacityWidget";
import TeamRosterWidget from "@/components/launchpad/TeamRosterWidget";
import StickiesWidget from "@/components/launchpad/StickiesWidget";

// Launchpad — a unified, branded launcher for every Whiplash Film & Creative tool.
//
// Lives at /launchpad AND is the post-login landing page. Project Manager is
// the active app, so it gets live data badges (active projects, conflict days)
// pulled from local endpoints. The three external apps are static cards — open
// in a new tab. To add live badges for them later, expose a /stats endpoint
// in each external app and wire it into externalBadges below.
//
// All cards share the same visual language: amber accent line, font-heading
// title, mono metadata. Internal Project Manager card has an Activity dot in
// the corner to signal "you're here".

const APPS = [
  {
    key: "projects",
    title: "Project Manager",
    tag: "Plan · Shoot · Resource",
    description:
      "Pre-production, call sheets, shot lists, plan timelines, and cross-project resourcing. The producer's command center.",
    href: "/",
    internal: true,
    icon: FolderKanban,
    accent: "#f59e0b", // amber-500
  },
  {
    key: "gear",
    title: "Gear Inventory",
    tag: "Cameras · Lenses · Audio",
    description:
      "Inventory of every camera body, lens, audio rig, and accessory. Bookings auto-sync from confirmed shoots.",
    href: "https://gear.whiplashfilmcreative.com",
    internal: false,
    icon: Package,
    accent: "#22d3ee", // cyan-400
  },
  {
    key: "dam",
    title: "DAM",
    tag: "Footage · Stills · Archive",
    description:
      "Digital asset library — searchable footage and stills with download / share controls.",
    href: "https://videos.whiplashfilmcreative.com/",
    internal: false,
    icon: Film,
    accent: "#a78bfa", // violet-400
  },
  {
    key: "personalized",
    title: "Personalized Video",
    tag: "Generate · Brand · Distribute",
    description:
      "Customer-facing tool for one-off personalized videos generated from a base template.",
    href: "https://makepersonalizedvideo.com/admin.html",
    internal: false,
    icon: Camera,
    accent: "#f472b6", // pink-400
  },
  {
    key: "setone",
    title: "Set.One Live",
    tag: "Live · Sync · Playback",
    description:
      "Live set control and playback companion for on-set music and audio cues.",
    href: "https://setonelive.app",
    internal: false,
    icon: Music,
    accent: "#34d399", // emerald-400
  },
  {
    key: "streamstop",
    title: "The Stream Stop",
    tag: "Broadcast · Route · Stream",
    description:
      "One-stop routing and broadcast control for outbound streams.",
    href: "https://thestreamstop.com",
    internal: false,
    icon: Airplay,
    accent: "#fbbf24", // amber-400
  },
  {
    key: "splashhub",
    title: "Splash Creative Hub",
    tag: "Create · Collaborate · Share",
    description:
      "Splash Creative's hub for creative resources, collaboration, and community.",
    href: "https://splashcreativehub.com/",
    internal: false,
    icon: Sparkles,
    accent: "#38bdf8", // sky-400
  },
  {
    key: "whiplashsite",
    title: "Whiplash Film & Creative",
    tag: "Studio · Portfolio · Contact",
    description:
      "The Whiplash Film & Creative website — our work, services, and studio story.",
    href: "https://www.whiplashfilmcreative.com",
    internal: false,
    icon: Globe,
    accent: "#fb923c", // orange-400
  },
];

export default function LaunchpadPage() {
  const { user } = useAuth();
  const { brand } = useBrand();
  const isAdmin = user?.role === "admin";
  // Managers get the enhanced Launchpad (Team Roster widget) alongside the
  // standard views. Admins get it too — they have full visibility anyway.
  const isManagerOrAbove = isAdmin || user?.role === "manager";
  const [projectsCount, setProjectsCount] = useState(null);
  const [conflictCount, setConflictCount] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    const tasks = [
      projectsApi.list().then((list) => mounted && setProjectsCount(list?.length ?? 0)).catch(() => {}),
    ];
    if (isAdmin) {
      const today = new Date().toISOString().slice(0, 10);
      const end = new Date(Date.now() + 28 * 86400000).toISOString().slice(0, 10);
      tasks.push(
        studioApi
          .getResources({ start: today, end })
          .then((d) => mounted && setConflictCount(d?.conflicts?.length ?? 0))
          .catch(() => {}),
      );
    }
    Promise.allSettled(tasks).finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [isAdmin]);

  // Greeting based on local time — small touch
  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 5) return "Still here";
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    if (h < 22) return "Good evening";
    return "Burning the midnight oil";
  }, []);
  const firstName = (user?.name || user?.email || "").split(/[ @]/)[0];

  return (
    <div
      className={`max-w-screen-2xl mx-auto px-6 lg:px-10 ${
        brand.polish?.spacious ? "py-16" : "py-12"
      }`}
      data-testid="launchpad-page"
    >
      {/* ── Hero ────────────────────────────────────────────── */}
      <div className={brand.polish?.spacious ? "mb-16" : "mb-12"}>
        <div
          className="text-overline mb-3"
          style={{ color: "var(--brand-accent)", opacity: 0.8 }}
        >
          {brand.portalTitle}
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-light text-zinc-100 leading-tight">
          {greeting}{firstName ? `, ${firstName}` : ""}.
        </h1>
        <p className="text-sm text-zinc-500 mt-3 max-w-xl">
          {brand.tagline}
        </p>
      </div>

      {/* ── Productivity Pulse (Feb 2026) ───────────────────── */}
      <ProductivityPulseWidget isAdmin={isAdmin} />

      {/* ── My Plate — cross-project personal task teaser (Feb 2026) ── */}
      <MyPlateWidget />

      {/* ── Waiting On — inverse of My Plate; tasks I've handed off (Iter 47) ── */}
      <WaitingOnWidget />

      {/* ── Team Capacity — this-week workload rollup (Jul 2026) ── */}
      <TeamCapacityWidget />

      {/* ── Team Roster — manager-only "who needs attention" view (Jul 2026) ── */}
      {isManagerOrAbove && <TeamRosterWidget />}

      {/* ── At-Risk triage queue (Feb 2026) ─────────────────── */}
      <AtRiskWidget />

      {/* ── Studio Insights — admin-only producer nudges (Feb 2026) ── */}
      <StudioInsightsWidget isAdmin={isAdmin} />

      {/* ── Stickies — personal quick-notes (Feb 2026) ──────── */}
      <StickiesWidget />

      {/* ── App Cards ─────────────────────────────────────────
          Admins see jump links to every Whiplash app. Editors/viewers see
          only the Project Manager "you're here" card — they don't need (or
          shouldn't see) the other tools. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {APPS.filter((app) => isAdmin || app.key === "projects").map((app) => {
          const isProjectMgr = app.key === "projects";
          const badges = [];
          if (isProjectMgr) {
            if (loading && projectsCount === null) {
              badges.push({ label: "loading…", muted: true });
            } else {
              badges.push({ label: `${projectsCount ?? 0} project${projectsCount === 1 ? "" : "s"}`, muted: false });
              if (isAdmin && conflictCount !== null) {
                badges.push({
                  label: `${conflictCount} conflict day${conflictCount === 1 ? "" : "s"}`,
                  warn: conflictCount > 0,
                });
              }
            }
          }
          return (
            <LaunchCard
              key={app.key}
              app={app}
              badges={badges}
              isProjectMgr={isProjectMgr}
            />
          );
        })}
      </div>

      <div className="mt-12 text-center text-[10px] font-mono text-zinc-600">
        Whiplash Film &amp; Creative · {new Date().getFullYear()}
      </div>
    </div>
  );
}

function LaunchCard({ app, badges, isProjectMgr }) {
  const Icon = app.icon;
  const inner = (
    <article
      data-testid={`launch-card-${app.key}`}
      className="group relative h-full border border-zinc-800 bg-zinc-950/60 hover:bg-zinc-900/70 rounded-md p-6 transition-colors overflow-hidden"
      style={{
        // Subtle accent line on the left
        borderLeftColor: app.accent,
        borderLeftWidth: 3,
      }}
    >
      {/* "You are here" pip */}
      {isProjectMgr && (
        <div className="absolute top-4 right-4 inline-flex items-center gap-1.5 text-[10px] font-mono text-amber-400 uppercase tracking-wider">
          <span className="relative flex w-1.5 h-1.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-60"></span>
            <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-amber-500"></span>
          </span>
          You&apos;re here
        </div>
      )}
      {/* External pip */}
      {!isProjectMgr && (
        <div className="absolute top-4 right-4 text-zinc-600 group-hover:text-zinc-400 transition-colors">
          <ExternalLink className="w-3.5 h-3.5" />
        </div>
      )}

      <div className="flex items-start gap-4">
        <div
          className="w-10 h-10 rounded-sm flex items-center justify-center shrink-0 border"
          style={{ borderColor: app.accent + "55", backgroundColor: app.accent + "1a" }}
        >
          <Icon className="w-5 h-5" style={{ color: app.accent }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-overline text-zinc-500 mb-1">{app.tag}</div>
          <h2 className="font-heading text-xl font-light text-zinc-100 leading-tight">
            {app.title}
          </h2>
        </div>
      </div>

      <p className="text-sm text-zinc-400 mt-4 leading-relaxed">
        {app.description}
      </p>

      {/* Badges row (live data for Project Manager) */}
      {badges.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-4" data-testid={`launch-card-badges-${app.key}`}>
          {badges.map((b, i) => (
            <span
              key={i}
              className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono rounded-sm border ${
                b.warn
                  ? "border-rose-900/60 bg-rose-950/30 text-rose-300"
                  : b.muted
                  ? "border-zinc-800 bg-zinc-950 text-zinc-600"
                  : "border-zinc-800 bg-zinc-900 text-zinc-400"
              }`}
            >
              {b.warn && <Activity className="w-2.5 h-2.5" />}
              {b.muted && <Loader2 className="w-2.5 h-2.5 animate-spin" />}
              {b.label}
            </span>
          ))}
        </div>
      )}

      {/* Open action */}
      <div className="flex items-center justify-end mt-6">
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-zinc-300 group-hover:text-amber-500 transition-colors">
          Open
          <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </span>
      </div>
    </article>
  );
  return app.internal ? (
    <Link to={app.href} className="block h-full" data-testid={`launch-link-${app.key}`}>
      {inner}
    </Link>
  ) : (
    <a
      href={app.href}
      target="_blank"
      rel="noreferrer"
      className="block h-full"
      data-testid={`launch-link-${app.key}`}
    >
      {inner}
    </a>
  );
}
